import React, { useMemo, useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SearchIcon, DownloadIcon, ArrowRightIcon, FilterIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { articles } from '../data/blogArticles';
import { clientCases as realClientCases } from '../data/clientCases';
type ItemType = 'blog' | 'seo' | 'case' | 'logo' | 'norden';
interface LibraryItem {
  id: string;
  title: string;
  category: string;
  description: string;
  image: string;
  link: string;
  type: ItemType;
  actionText: string;
}
// 1. Blog Posts (from BlogSection.tsx)
const blogPosts: LibraryItem[] = [{
  id: 'blog-1',
  title: 'Klanten werven als financieel adviseur: voorbij de via-via',
  category: 'B2B Groei',
  description: 'Afhankelijk van doorverwijzingen? Ontdek hoe je structureel nieuwe klanten aantrekt buiten je bestaande netwerk. Wij gebruiken hiervoor een data-gedreven aanpak.',
  image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  link: '/seo/blog/klanten-werven-financieel-adviseur',
  type: 'blog',
  actionText: 'Lees artikel'
}, {
  id: 'blog-2',
  title: 'Waarom data-gedreven marketing werkt voor kleine B2B-bedrijven',
  category: 'Strategie',
  description: 'Geen budget om te gokken? Ontdek waarom eerst testen en dan bouwen de slimste investering is voor bedrijven met 1-5 medewerkers.',
  image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  link: '/seo/blog/data-gedreven-marketing-mkb',
  type: 'blog',
  actionText: 'Lees artikel'
}, {
  id: 'blog-3',
  title: 'Je website als vangnet: waarom een visitekaartje-site niet genoeg is',
  category: 'Websites',
  description: 'Een mooie website is niet genoeg. Ontdek waarom je website moet functioneren als een vangnet dat actief klanten binnenhaalt.',
  image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  link: '/seo/blog/website-als-vangnet',
  type: 'blog',
  actionText: 'Lees artikel'
}];
// 2. Client Cases (from data/clientCases.ts)
const clientCases: LibraryItem[] = realClientCases.map(c => ({
  id: `case-${c.slug}`,
  title: c.clientName,
  category: c.sector,
  description: c.shortDescription,
  image: c.heroImage,
  link: `/cases/${c.slug}`,
  type: 'case',
  actionText: 'Bekijk case'
}));
// 3. SEO Articles (from data/blogArticles.ts, includes all 64 articles)
const seoArticles: LibraryItem[] = articles.map(article => ({
  id: `seo-${article.slug}`,
  title: article.title,
  category: article.cluster,
  description: article.metaDescription,
  image: 'https://images.unsplash.com/photo-1432821596592-e2c18b78144f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  link: `/seo/blog/${article.slug}`,
  type: 'seo',
  actionText: 'Lees artikel'
}));
// 4. Logo's & Branding
const NORDEN_PORTRAIT = 'https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png';
const logos: LibraryItem[] = [{
  id: 'logo-portret',
  title: 'Noord Digital Logo (Norden portret)',
  category: 'Branding',
  description: 'Het primaire Noord Digital logo: het portret van Norden in 300 dpi. Geschikt voor lichte achtergronden, social, presentaties en e-mailhandtekeningen.',
  image: NORDEN_PORTRAIT,
  link: NORDEN_PORTRAIT,
  type: 'logo',
  actionText: 'Download afbeelding'
}];
// 5. Norden Mascotte
const nordenAssets: LibraryItem[] = [{
  id: 'norden-portret',
  title: 'Norden Portret',
  category: 'Mascotte',
  description: "Hoge resolutie portret van Norden de ijsbeer. Perfect voor teampagina's, avatars en algemene communicatie.",
  image: NORDEN_PORTRAIT,
  link: NORDEN_PORTRAIT,
  type: 'norden',
  actionText: 'Download afbeelding'
}, {
  id: 'norden-fullbody',
  title: 'Norden Full Body',
  category: 'Mascotte',
  description: 'Volledige weergave van Norden, gespiegeld. Ideaal voor presentaties, banners en grote visuele elementen.',
  image: 'https://cdn.magicpatterns.com/uploads/aj1Btym2B39xizjMwoi1Gy/Norden_full_body_gespiegeld.png',
  link: 'https://cdn.magicpatterns.com/uploads/aj1Btym2B39xizjMwoi1Gy/Norden_full_body_gespiegeld.png',
  type: 'norden',
  actionText: 'Download afbeelding'
}, {
  id: 'norden-kleurwaaier',
  title: 'Norden met kleurwaaier',
  category: 'Mascotte',
  description: 'Norden met kleurwaaier. Gebruikt in het werkproces voor de fase ontwerp en huisstijl.',
  image: 'https://cdn.magicpatterns.com/uploads/hr1JC9wRjcR2KJD3mjAfSG/Norden_met_kleur_waaier.png',
  link: 'https://cdn.magicpatterns.com/uploads/hr1JC9wRjcR2KJD3mjAfSG/Norden_met_kleur_waaier.png',
  type: 'norden',
  actionText: 'Download afbeelding'
}, {
  id: 'norden-tandwiel',
  title: 'Norden met tandwiel',
  category: 'Mascotte',
  description: 'Norden met tandwiel. Symboliseert techniek, optimalisatie en ontwikkeling van de website.',
  image: 'https://cdn.magicpatterns.com/uploads/k2oc6i7aAK288UUd8m2sTF/Norden_met_tandwiel.png',
  link: 'https://cdn.magicpatterns.com/uploads/k2oc6i7aAK288UUd8m2sTF/Norden_met_tandwiel.png',
  type: 'norden',
  actionText: 'Download afbeelding'
}, {
  id: 'norden-raket',
  title: 'Norden met raket',
  category: 'Mascotte',
  description: 'Norden met raket. Past bij lancering, livegang en groei. Goed voor launch-content en succes-momenten.',
  image: 'https://cdn.magicpatterns.com/uploads/1WqxCzJ38hFJxSHm98buvx/Norden_met_raket.png',
  link: 'https://cdn.magicpatterns.com/uploads/1WqxCzJ38hFJxSHm98buvx/Norden_met_raket.png',
  type: 'norden',
  actionText: 'Download afbeelding'
}, {
  id: 'norden-plant',
  title: 'Norden met plant',
  category: 'Mascotte',
  description: 'Norden met plant. Staat voor groei, onderhoud en de fase waarin de website blijft bloeien.',
  image: 'https://cdn.magicpatterns.com/uploads/3MuSnfaWEHYo1WZXKtBhAK/Norden_met_plant.png',
  link: 'https://cdn.magicpatterns.com/uploads/3MuSnfaWEHYo1WZXKtBhAK/Norden_met_plant.png',
  type: 'norden',
  actionText: 'Download afbeelding'
}, {
  id: 'norden-cookie',
  title: 'Norden eet een cookie',
  category: 'Mascotte',
  description: 'Norden die smikkelt van een koekje. Speciaal voor de cookie-banner en luchtige content over privacy.',
  image: 'https://cdn.magicpatterns.com/uploads/a7QwrfsRheFwXZaY2yk98u/Norden_die_cookie_eet_nom.png',
  link: 'https://cdn.magicpatterns.com/uploads/a7QwrfsRheFwXZaY2yk98u/Norden_die_cookie_eet_nom.png',
  type: 'norden',
  actionText: 'Download afbeelding'
}];
const allItems: LibraryItem[] = [...blogPosts, ...clientCases, ...seoArticles, ...logos, ...nordenAssets];
const categories = [{
  id: 'all',
  label: 'Alle',
  types: ['blog', 'seo', 'case', 'logo', 'norden']
}, {
  id: 'blog',
  label: 'Blog Posts',
  types: ['blog']
}, {
  id: 'seo',
  label: 'SEO Artikelen',
  types: ['seo']
}, {
  id: 'case',
  label: 'Klant Cases',
  types: ['case']
}, {
  id: 'logo',
  label: "Logo's & Branding",
  types: ['logo']
}, {
  id: 'norden',
  label: 'Norden Mascotte',
  types: ['norden']
}];
export const BibliotheekPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const filteredItems = useMemo(() => {
    const activeTypes = categories.find(c => c.id === activeCategory)?.types || [];
    return allItems.filter(item => {
      const matchesType = activeTypes.includes(item.type);
      const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) || item.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesType && matchesSearch;
    });
  }, [activeCategory, searchQuery]);
  return <div className="min-h-screen bg-[#F7F8FA] flex flex-col" data-id="element-926">
      <Header data-id="element-927" />

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#007AFF] to-[#1B2B44] pt-32 pb-20 relative overflow-hidden" data-id="element-928">
        <div className="absolute inset-0 opacity-10" data-id="element-929">
          <div className="w-full h-full bg-[url('https://images.unsplash.com/photo-1507842217343-583bb7270b66?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center" data-id="element-930"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] relative z-10 text-center" data-id="element-931">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-id="element-932">
            Bibliotheek
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-white/90 max-w-2xl mx-auto" data-id="element-933">
            Alle kennis, cases en merkbestanden van Noord Digital verzameld op
            één plek.
          </motion.p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] py-12 flex-grow" data-id="element-934">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-935">
          {/* Mobile Filter Toggle */}
          <div className="lg:hidden mb-4" data-id="element-936">
            <button onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)} className="w-full bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between font-semibold text-[#111827] shadow-sm" data-id="element-937">
              <span className="flex items-center gap-2" data-id="element-938">
                <FilterIcon className="w-5 h-5 text-[#007AFF]" data-id="element-939" />
                Filters & Categorieën
              </span>
              <span className="text-sm text-gray-500" data-id="element-940">
                {categories.find(c => c.id === activeCategory)?.label}
              </span>
            </button>
          </div>

          {/* Sidebar */}
          <div className={`lg:w-64 flex-shrink-0 ${isMobileFiltersOpen ? 'block' : 'hidden lg:block'}`} data-id="element-941">
            <div className="sticky top-24 bg-white rounded-2xl shadow-sm border border-gray-100 p-6" data-id="element-942">
              <h3 className="font-bold text-[#111827] mb-4 text-lg" data-id="element-943">
                Categorieën
              </h3>
              <div className="space-y-2" data-id="element-944">
                {categories.map(category => <button key={category.id} onClick={() => {
                setActiveCategory(category.id);
                setIsMobileFiltersOpen(false);
              }} className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 font-medium ${activeCategory === category.id ? 'bg-[#007AFF] text-white shadow-md' : 'text-[#6B7280] hover:bg-gray-50 hover:text-[#111827]'}`} data-id="element-945">
                    {category.label}
                    <span className={`float-right text-xs py-1 px-2 rounded-full ${activeCategory === category.id ? 'bg-white/20' : 'bg-gray-100 text-gray-500'}`} data-id="element-946">
                      {category.id === 'all' ? allItems.length : allItems.filter(item => category.types.includes(item.type)).length}
                    </span>
                  </button>)}
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1" data-id="element-947">
            {/* Search Bar */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-2 mb-8 flex items-center" data-id="element-948">
              <div className="pl-4 pr-2" data-id="element-949">
                <SearchIcon className="w-5 h-5 text-gray-400" data-id="element-950" />
              </div>
              <input type="text" placeholder="Zoek in artikelen, cases of bestanden..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full py-3 px-2 outline-none text-[#111827] placeholder-gray-400 bg-transparent" data-id="element-951" />
              {searchQuery && <button onClick={() => setSearchQuery('')} className="px-4 text-sm text-gray-400 hover:text-gray-600" data-id="element-952">
                  Wis
                </button>}
            </div>

            {/* Results Grid */}
            {filteredItems.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-id="element-953">
                <AnimatePresence mode="popLayout" data-id="element-954">
                  {filteredItems.map(item => <motion.div layout initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.9
              }} transition={{
                duration: 0.2
              }} key={item.id} className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col group" data-id="element-955">
                      {/* Image */}
                      <div className="relative h-48 overflow-hidden bg-gray-100" data-id="element-956">
                        <img src={item.image} alt={item.title} className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${item.type === 'logo' || item.type === 'norden' ? 'object-contain p-4' : ''}`} data-id="element-957" />
                        <div className="absolute top-4 left-4" data-id="element-958">
                          <span className={`text-xs font-semibold px-3 py-1 rounded-full text-white ${item.type === 'blog' ? 'bg-[#007AFF]' : item.type === 'seo' ? 'bg-[#1B2B44]' : item.type === 'case' ? 'bg-emerald-500' : 'bg-purple-500'}`} data-id="element-959">
                            {item.category}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6 flex flex-col flex-grow" data-id="element-960">
                        <h3 className="font-bold text-[#111827] mb-3 text-lg line-clamp-2 group-hover:text-[#007AFF] transition-colors" data-id="element-961">
                          {item.title}
                        </h3>
                        <p className="text-[#6B7280] mb-6 line-clamp-3 text-sm flex-grow" data-id="element-962">
                          {item.description}
                        </p>

                        <a href={item.link} className={`inline-flex items-center justify-center w-full py-3 px-4 rounded-lg font-medium transition-all duration-300 ${item.type === 'logo' || item.type === 'norden' ? 'bg-purple-50 text-purple-600 hover:bg-purple-100' : 'bg-blue-50 text-[#007AFF] hover:bg-[#007AFF] hover:text-white'}`} data-id="element-963">
                          {item.type === 'logo' || item.type === 'norden' ? <>
                              <DownloadIcon className="w-4 h-4 mr-2" data-id="element-964" />
                              {item.actionText}
                            </> : <>
                              {item.actionText}
                              <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-965" />
                            </>}
                        </a>
                      </div>
                    </motion.div>)}
                </AnimatePresence>
              </div> : <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-sm" data-id="element-966">
                <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-967">
                  <SearchIcon className="w-8 h-8 text-gray-400" data-id="element-968" />
                </div>
                <h3 className="text-xl font-bold text-[#111827] mb-2" data-id="element-969">
                  Geen resultaten gevonden
                </h3>
                <p className="text-[#6B7280]" data-id="element-970">
                  We konden geen items vinden die overeenkomen met "
                  {searchQuery}". Probeer een andere zoekterm of categorie.
                </p>
                <button onClick={() => {
              setSearchQuery('');
              setActiveCategory('all');
            }} className="mt-6 text-[#007AFF] font-medium hover:underline" data-id="element-971">
                  Wis alle filters
                </button>
              </div>}
          </div>
        </div>
      </div>

      <Footer data-id="element-972" />
    </div>;
};